import { useState } from "react";
import { motion } from "framer-motion";
import { Eye, Video, Megaphone, Users, Loader2, CheckCircle2, Zap } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { SectionHeading } from "@/components/SectionHeading";
import { Marquee } from "@/components/Marquee";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PHOTOS } from "@/lib/assets";
import { postPartnershipInquiry } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const REACH_ICONS = [Eye, Users, Megaphone, Video];
const TIER_ACCENTS = ["blue", "acid", "blue"];

export default function Partnership() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useLang();
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    partnership_type: "",
    budget: "",
    message: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email || !form.partnership_type || !form.message) {
      toast.error(t("partnership.errRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await postPartnershipInquiry(form);
      setSuccess(true);
      toast.success(t("partnership.success"));
    } catch {
      toast.error(t("partnership.errSubmit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrap testId="partnership-page">
      {/* hero */}
      <section className="relative overflow-hidden pt-36 pb-16">
        <div className="absolute inset-0">
          <img src={PHOTOS.smokeBarrier} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#07080A]/70 via-[#07080A]/85 to-[#07080A]" />
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// {t("partnership.kicker")}</div>
            <h1 data-testid="partnership-headline" className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-[0.03em] sm:text-8xl">
              {t("partnership.title1")}<span className="text-[#1EA7FF]">{t("partnership.title2")}</span>
            </h1>
            <p className="mt-5 max-w-xl leading-relaxed text-white/55">
              {t("partnership.intro")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* reach stats */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div data-testid="partnership-reach-grid" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {t("partnership.reach").map((r, i) => {
            const Icon = REACH_ICONS[i];
            return (
              <motion.div
                key={r.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.08 * i }}
                className="clip-corner-sm border border-white/10 bg-[#0F1217] p-5"
              >
                <Icon className="h-5 w-5 text-[#1EA7FF]" />
                <div className="mt-3 font-mono text-3xl font-semibold text-white">{r.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.14em] text-white/45">{r.label}</div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* offers */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionHeading kicker={t("partnership.offersKicker")} title={t("partnership.offersTitle")} testId="partnership-offers-heading" />
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {t("partnership.offers").map((o, i) => (
            <motion.div
              key={o.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className="light-sweep clip-corner border border-white/10 bg-[#0F1217] p-6 transition-colors duration-200 hover:border-[#1EA7FF]/40"
            >
              <div className="font-display text-2xl uppercase tracking-[0.05em]">{o.title}</div>
              <p className="mt-3 text-sm leading-relaxed text-white/50">{o.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Marquee items={t("marquee.a")} accent />

      {/* tiers */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionHeading kicker={t("partnership.tiersKicker")} title={t("partnership.tiersTitle")} accent="acid" testId="partnership-tiers-heading" />
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {t("partnership.tiers").map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className={`clip-corner relative border bg-[#0F1217] p-6 ${
                i === 1 ? "border-[#B7FF2A]/50 neon-edge-acid" : "border-white/10"
              }`}
            >
              {i === 1 && (
                <span className="absolute right-4 top-4 flex items-center gap-1 bg-[#B7FF2A] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#07080A]">
                  <Zap className="h-3 w-3" /> {t("partnership.mostCommon")}
                </span>
              )}
              <div className="font-display text-3xl uppercase tracking-[0.05em]">{tier.name}</div>
              <ul className="mt-5 space-y-2.5">
                {tier.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-white/60">
                    <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 ${i === 1 ? "bg-[#B7FF2A]" : "bg-[#1EA7FF]"}`} />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* form */}
      <section className="border-t border-white/10 bg-[#0B0D10] py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker={t("partnership.formKicker")} title={t("partnership.formTitle")} align="center" testId="partnership-form-heading" />
          <div className="mt-10">
            {success ? (
              <motion.div
                data-testid="partnership-inquiry-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="clip-corner flex flex-col items-center gap-4 border border-[#B7FF2A]/30 bg-[#0F1217] p-10 text-center"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#B7FF2A]/10">
                  <CheckCircle2 className="h-8 w-8 text-[#B7FF2A]" />
                </div>
                <div className="font-display text-3xl uppercase tracking-[0.06em]">{t("partnership.successTitle")}</div>
                <p className="max-w-sm text-sm leading-relaxed text-white/55">
                  {t("partnership.successText")}
                </p>
                <Button
                  data-testid="partnership-inquiry-new-button"
                  variant="outline"
                  onClick={() => {
                    setSuccess(false);
                    setForm({ company: "", name: "", email: "", partnership_type: "", budget: "", message: "" });
                  }}
                  className="clip-corner-sm border-white/20 font-display uppercase tracking-[0.14em] hover:bg-white/5"
                >
                  {t("partnership.sendAnother")}
                </Button>
              </motion.div>
            ) : (
              <form
                data-testid="partnership-inquiry-form"
                onSubmit={submit}
                className="clip-corner space-y-4 border border-white/10 bg-[#0F1217] p-6 sm:p-8"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input data-testid="partnership-company-input" placeholder={t("partnership.phCompany")} value={form.company} onChange={set("company")} className="h-11 border-white/15 bg-[#0B0D10]" />
                  <Input data-testid="partnership-name-input" placeholder={t("partnership.phContact")} value={form.name} onChange={set("name")} className="h-11 border-white/15 bg-[#0B0D10]" />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input data-testid="partnership-email-input" type="email" placeholder={t("partnership.phEmail")} value={form.email} onChange={set("email")} className="h-11 border-white/15 bg-[#0B0D10]" />
                  <Select value={form.partnership_type} onValueChange={(v) => setForm((f) => ({ ...f, partnership_type: v }))}>
                    <SelectTrigger data-testid="partnership-type-select" className="h-11 border-white/15 bg-[#0B0D10]">
                      <SelectValue placeholder={t("partnership.phType")} />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0F1217]">
                      {t("partnership.types").map((typ) => (
                        <SelectItem key={typ} value={typ}>{typ}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                  <SelectTrigger data-testid="partnership-budget-select" className="h-11 border-white/15 bg-[#0B0D10]">
                    <SelectValue placeholder={t("partnership.phBudget")} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0F1217]">
                    {t("partnership.budgets").map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  data-testid="partnership-message-textarea"
                  placeholder={t("partnership.phMessage")}
                  value={form.message}
                  onChange={set("message")}
                  className="min-h-32 border-white/15 bg-[#0B0D10]"
                />
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="partnership-inquiry-submit-button"
                  className="light-sweep clip-corner-sm h-12 w-full bg-[#B7FF2A] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#B7FF2A] hover:brightness-110 disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("partnership.sending")}</span>
                  ) : (
                    t("partnership.submit")
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageWrap>
  );
}
